import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, Rate, Spin } from 'antd';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import api from '../config/api';
import GrammarCheckedTextArea from '../components/GrammarCheckedTextArea';

const { Title, Text } = Typography;

const Feedback: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  // Logo URL with cache busting
  const logoUrl = React.useMemo(() => {
    const basePath = process.env.PUBLIC_URL || '';
    const cacheBuster = `${Date.now()}_${Math.random()}`;
    return `${basePath}/logo.png?v=${cacheBuster}`.replace('//', '/');
  }, []);

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Fetch feedback details
  const { data: feedbackData, isLoading, error } = useQuery(
    ['feedback', token],
    async () => {
      const response = await api.get(`/api/feedback/${token}`);
      return response.data;
    },
    {
      enabled: !!token,
      retry: false
    }
  );

  // Submit feedback mutation
  const submitFeedbackMutation = useMutation(
    async (data: { rating: number; comment: string }) => {
      const response = await api.post(`/api/feedback/${token}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        navigate('/feedback/thank-you');
      }
    }
  );

  const handleSubmit = (values: { comment?: string }) => {
    if (rating < 1 || rating > 5) {
      return;
    }
    submitFeedbackMutation.mutate({ rating, comment: values.comment || '' });
  };

  const getRatingLabel = (value: number) => {
    const labels: { [key: number]: string } = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[value] || '';
  };

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !feedbackData?.feedback) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 24,
            padding: '56px 48px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Alert
            message="Feedback Not Found"
            description="This feedback link is invalid or has expired. Please contact support if you believe this is an error."
            type="error"
            showIcon
          />
        </div>
      </div>
    );
  }

  const feedback = feedbackData.feedback;

  if (feedback.is_submitted) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 24,
            padding: '56px 48px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Alert
            message="Thank You!"
            description="Your feedback has already been submitted. We appreciate your input!"
            type="success"
            showIcon
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
          `,
          opacity: 0.6,
        }}
      />

      {/* Centered Feedback Card */}
      <div
        style={{
          width: '100%',
          maxWidth: 600,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: 24,
          padding: '56px 48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          zIndex: 1,
          animation: 'fadeInUp 0.6s ease-out',
        }}
      >
        {/* Logo/Branding at top */}
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          {logoUrl ? (
            <img
              key={logoUrl}
              src={logoUrl}
              alt="Vitreous Support Logo"
              style={{
                maxWidth: '240px',
                height: 'auto',
                objectFit: 'contain',
                margin: '0 auto 24px',
                transition: 'opacity 0.3s ease',
              }}
              onError={(e) => {
                console.error('[Feedback] Failed to load logo from:', logoUrl);
              }}
              onLoad={() => {
                console.log('[Feedback] Logo loaded successfully from:', logoUrl);
              }}
            />
          ) : (
            <Title level={1} style={{ fontSize: 32, fontWeight: 700, margin: '0 0 24px', color: '#1a1a1a', letterSpacing: '-0.5px' }}>
              Vitreous Support
            </Title>
          )}
          <Title
            level={2}
            style={{
              fontSize: 28,
              fontWeight: 600,
              marginBottom: 8,
              color: '#1a1a1a',
              lineHeight: 1.3,
            }}
          >
            How was your support experience?
          </Title>
          <Text type="secondary" style={{ fontSize: 15 }}>
            Your feedback helps us improve our service quality
          </Text>
        </div>

        {/* Error Alert */}
        {submitFeedbackMutation.isError && (
          <Alert
            message="Submission Failed"
            description="Failed to submit feedback. Please try again."
            type="error"
            showIcon
            closable
            style={{
              marginBottom: 28,
              borderRadius: 12,
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
            }}
          />
        )}

        {/* Rating Section */}
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 8,
                  transition: 'transform 0.2s ease',
                  transform: hoveredRating >= star || rating >= star ? 'scale(1.2)' : 'scale(1)',
                }}
              >
                {(hoveredRating >= star || rating >= star) ? (
                  <StarFilled
                    style={{
                      fontSize: 48,
                      color: '#FFD700',
                      filter: 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3))',
                    }}
                  />
                ) : (
                  <StarOutlined
                    style={{
                      fontSize: 48,
                      color: '#d9d9d9',
                    }}
                  />
                )}
              </button>
            ))}
          </div>
          {rating > 0 && (
            <Text
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: rating >= 4 ? '#52c41a' : rating >= 3 ? '#faad14' : '#ff4d4f',
                display: 'block',
                marginTop: 8,
              }}
            >
              {getRatingLabel(rating)}
            </Text>
          )}
        </div>

          {/* Feedback Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ width: '100%' }}
        >
          <Form.Item
            name="comment"
            label={<Text strong style={{ fontSize: 15 }}>Additional Comments (Optional)</Text>}
            style={{ marginBottom: 32 }}
          >
            <GrammarCheckedTextArea
              rows={5}
              placeholder="Tell us more about your experience..."
              style={{
                borderRadius: 12,
                fontSize: 15,
                border: '1.5px solid #e8e8e8',
                transition: 'all 0.2s ease',
                padding: '12px 16px',
              }}
              onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                e.target.style.borderColor = '#000';
                e.target.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.05)';
              }}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                e.target.style.borderColor = '#e8e8e8';
                e.target.style.boxShadow = 'none';
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitFeedbackMutation.isLoading}
              disabled={rating < 1}
              block
              style={{
                height: 52,
                fontSize: 16,
                fontWeight: 600,
                borderRadius: 12,
                background: rating >= 1
                  ? 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)'
                  : '#d9d9d9',
                border: 'none',
                transition: 'all 0.2s ease',
                boxShadow: rating >= 1 ? '0 4px 12px rgba(30, 58, 138, 0.3)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (rating >= 1) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (rating >= 1) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.3)';
                }
              }}
            >
              {submitFeedbackMutation.isLoading ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Feedback;
